import { useEffect, useState } from 'react';
import type { Color, Look } from '../../shared/protocol.ts';
import { useT } from '../app/preferences.ts';
import { PlayerAvatar, PlayerName } from './PlayerLook.tsx';

export interface VsPlayer {
  name: string;
  rating: number | null;
  look: Look | null;
}

/** Face-off before a game: both players side by side, "VS", then a countdown. */
export function VsIntro({
  title,
  me,
  myColor,
  opponent,
  seconds,
  onDone,
}: {
  title: string;
  me: VsPlayer;
  myColor: Color;
  opponent: VsPlayer;
  seconds: number;
  onDone: () => void;
}) {
  const t = useT();
  const [left, setLeft] = useState(seconds);

  useEffect(() => {
    if (left <= 0) {
      onDone();
      return;
    }
    const id = window.setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [left, onDone]);

  const side = (who: VsPlayer, color: Color, mine: boolean) => (
    <div className={`vs-player${mine ? ' mine' : ''}`}>
      <PlayerAvatar look={who.look} name={who.name} size="xl" />
      <strong className="vs-name">
        <PlayerName name={who.name} look={who.look} />
      </strong>
      {who.rating !== null && <span className="badge badge-accent">🏆 {who.rating}</span>}
      <span className={`vs-color ${color}`}>
        <span className="vs-color-dot" aria-hidden="true" />
        {t(`color.${color}`)}
        {mine && ` · ${t('game.you')}`}
      </span>
    </div>
  );

  return (
    <div className="card match-intro pop-in" role="status" aria-live="polite">
      <h1 className="match-title">{title}</h1>
      <div className="vs-row">
        {side(me, myColor, true)}
        <span className="vs-badge" aria-hidden="true">
          VS
        </span>
        {side(opponent, myColor === 'w' ? 'b' : 'w', false)}
      </div>
      <div className="match-countdown">
        <span>{t('online.startsIn')}</span>
        <span className="countdown-number" key={left}>
          {Math.max(left, 1)}
        </span>
      </div>
    </div>
  );
}
